import { message, FormInstance } from 'antd'

import { getSeconds } from '~/lib/durationDisplay'
import Subset from '~/lib/Subset'
import {
    DOMESTIC_PRIORITIES,
    OVERSEAS_PRIORITIES,
    PRIORITYID_RUSH,
    PRIORITYID_STANDARD,
    PRIORITYID_ECONOMY,
    PRIORITYID_FLEX,
    PRIORITYID_SAVER,
    PRIORITYID_OVERSEAS,
    PRIORITYID_SAMEDAY,
    PRIORITYID_PREMIUM,
    PROCESSID_3DP,
} from '~/lib/partsConstants'
import {
    AdvancedInspectionDataInput,
    DfmTags,
    DomesticAndOverseasCostDataInput,
    DomesticAndOverseasMaterialStockDataInput,
    InspectionDataInput,
    PendingMarkupDataInput,
    ScrapRateDataInput,
} from '~/__generated__/globalTypes'
import {
    AdminQuoteModelRevisionDataFragment as QuoteModelRevision,
    ProcessesDetailsDeprecatedSecondaryProcessFragment as SecondaryProcessesType,
    DfmFeedbackDeprecatedFragment as DfmFeedbackType,
} from '~/gql/graphql'

import {
    ADVANCED_INSPECTION_FORM_KEYS,
    DEFAULT_ADVANCED_INPECTION_MARKUP_PERCENTAGE,
    DFM_TAGS,
    DFM_TAGS_MAP,
    OPERATION_REGION_CHINA,
    OPERATION_REGION_INDIA,
    OPERATION_REGION_USA,
    QUOTING_FORM_KEYS,
    QuotingFormKeysType,
} from './constants'
import { generateStepItems } from './components/EdmCalculatorPanel/logic'
import { calculateQuoteModelRevisionPendingPrices_calculateQuoteModelRevisionPendingPrices_markupData as MarkupDataType } from './components/PartPricingPanel/__generated__/calculateQuoteModelRevisionPendingPrices'

const MIN_MARKUP = 0.43
const CENTS_PER_DOLLAR = 100

export const DEFAULT_DAYS_BY_PRIORITY_ID = {
    rush: 3,
    standard: 5,
    economy: 7,
    flex: 4,
    saver: 7,
    overseas: 12,
}

interface MaterialStockInterface {
    lathe?: Record<string, any>
    mill?: Record<string, any>
}

interface MaterialStocksInterface {
    usa?: MaterialStockInterface
    china?: MaterialStockInterface
}

interface DomesticAndOverseasCostInterface {
    usa?: number
    china?: number
}

interface SecondaryProcessesSubset
    extends Subset<
        SecondaryProcessesType,
        {
            process: {
                id: string
            }
        }
    > {}

interface DfmFeedbackSubset
    extends Subset<DfmFeedbackType, { message: string }> {}

export interface QMRSubset
    extends Subset<
        QuoteModelRevision,
        {
            id: string
            processesDetails: {
                secondaryProcesses: SecondaryProcessesSubset[]
            } | null
            dfmFeedback: DfmFeedbackSubset[]
            modelConfiguration: {
                dfmInspection: {
                    inspectionData: {
                        miscNonRecurringEngineeringCostInCents: number
                        miscProductionCostInCents: number
                        machineTimeMultiplier: number
                        dfmTags: DfmTags[]
                        edmProductionCostsInCents?: DomesticAndOverseasCostDataInput | null
                        materialCostsInCents?: DomesticAndOverseasCostDataInput | null
                        materialStocks?: DomesticAndOverseasMaterialStockDataInput | null
                        reamerCostsInCents?: DomesticAndOverseasCostDataInput | null
                        scrapMaterialCostsInCents?: DomesticAndOverseasCostDataInput | null
                        tapToolCostsInCents?: DomesticAndOverseasCostDataInput | null
                        toolCostsInCents?: DomesticAndOverseasCostDataInput | null
                        undercuttingToolCostsInCents?: DomesticAndOverseasCostDataInput | null
                    } & { [key: string]: any }
                } | null
                process?: {
                    id: string
                } | null
            } | null
        }
    > {}

export const isNumber = (x: any) => typeof x === 'number' && Number.isFinite(x)

const cantReleaseWarning = (reason: string) =>
    message.warning(
        `Inspection and DFM not released due to incorrect ${reason} input.`,
    )

const cantCalculateWarning = (reason: string) =>
    message.warning(`Cannot calculate due to incorrect ${reason} input.`)

export const hasMaterialStockCost = ({
    dfmTags,
    materialStocks,
    materialStock,
    overseasMaterialStock,
    materialCostsInCents,
    materialCostInCents,
    overseasMaterialCostInCents,
    isOverseas,
}: {
    dfmTags: string[]
    materialStocks?: MaterialStocksInterface
    materialStock?: MaterialStockInterface
    overseasMaterialStock?: MaterialStockInterface
    materialCostsInCents?: DomesticAndOverseasCostInterface
    materialCostInCents?: number
    overseasMaterialCostInCents?: number
    isOverseas: boolean
}) => {
    // At this point we've UPPERCASED our dfm tags apparently
    let materialStockObject

    if (dfmTags.includes('LATHE')) {
        materialStockObject = isOverseas
            ? materialStocks?.china?.lathe || overseasMaterialStock?.lathe
            : materialStocks?.usa?.lathe || materialStock?.lathe
    } else {
        materialStockObject = isOverseas
            ? materialStocks?.china?.mill || overseasMaterialStock?.mill
            : materialStocks?.usa?.mill || materialStock?.mill
    }

    const costInCents = isOverseas
        ? materialCostsInCents?.china || overseasMaterialCostInCents
        : materialCostsInCents?.usa || materialCostInCents

    if (costInCents && Number.isFinite(costInCents)) {
        return costInCents > 0
    }

    return materialStockObject?.stockPricePerUnitInCents > 0
}

export const hasMaterialStockCostForQuotedRegions = ({
    dfmTags,
    allowedPriorityIds,
    materialCostInCents,
    materialCostsInCents,
    overseasMaterialCostInCents,
    materialStock,
    overseasMaterialStock,
    materialStocks,
}: {
    dfmTags: string[]
    allowedPriorityIds: string[]
    materialCostsInCents?: DomesticAndOverseasCostInterface
    materialCostInCents?: number
    overseasMaterialCostInCents?: number
    materialStock: MaterialStockInterface
    overseasMaterialStock: MaterialStockInterface
    materialStocks?: MaterialStocksInterface
}) => {
    const hasDomesticMaterial = hasMaterialStockCost({
        dfmTags,
        materialStocks,
        materialStock,
        materialCostsInCents,
        materialCostInCents,
        isOverseas: false,
    })

    const hasOverseasMaterial = hasMaterialStockCost({
        dfmTags,
        materialStocks,
        overseasMaterialStock,
        materialCostsInCents,
        overseasMaterialCostInCents,
        isOverseas: true,
    })

    const needsDomesticMaterial = DOMESTIC_PRIORITIES.some(domesticPriority =>
        allowedPriorityIds.includes(domesticPriority),
    )

    const needsOverseasMaterial = OVERSEAS_PRIORITIES.some(overseasPriority =>
        allowedPriorityIds.includes(overseasPriority),
    )

    return (
        (!needsDomesticMaterial || hasDomesticMaterial) &&
        (!needsOverseasMaterial || hasOverseasMaterial)
    )
}

export const hasAcceptableMarkups = (
    markupData: PendingMarkupDataInput | null = {},
    minMarkupFlagPct: number | null,
): boolean => {
    // null check
    if (!markupData) {
        return true
    }

    let numMarkupsLessThanMinimum = 0
    const minMarkup =
        typeof minMarkupFlagPct === 'number'
            ? minMarkupFlagPct / 100
            : MIN_MARKUP

    for (const [, value] of Object.entries(markupData)) {
        if ((value || 0) < minMarkup && value !== null) {
            ++numMarkupsLessThanMinimum
        }
    }

    return numMarkupsLessThanMinimum === 0
}

export const isAdvancedInspectionDataValid = (
    advancedInspectionData?: Record<string, any> | null,
) => {
    if (!advancedInspectionData) {
        return true
    }

    const {
        numInspectionPoints,
        numSpecialInspectionPoints,
        percentMarkup,
        domesticLeadTimeDays,
        overseasLeadTimeDays,
    } = advancedInspectionData

    const isValidDomesticLeadTime = domesticLeadTimeDays
        ? isNumber(domesticLeadTimeDays)
        : true
    const isValidOverseasLeadTime = overseasLeadTimeDays
        ? isNumber(overseasLeadTimeDays)
        : true

    return Boolean(
        isNumber(numInspectionPoints) &&
            isNumber(numSpecialInspectionPoints) &&
            isNumber(percentMarkup) &&
            (numInspectionPoints || numSpecialInspectionPoints) &&
            isValidDomesticLeadTime &&
            isValidOverseasLeadTime,
    )
}

export const isHardwareInstallDataValid = (
    inspectionData: InspectionDataInput,
    secondaryProcesses: SecondaryProcessesSubset[],
    dfmFeedback: DfmFeedbackSubset[],
) => {
    const hardwareBoxChecked = (inspectionData.dfmTags || []).find(
        tag => tag === DFM_TAGS.HARDWARE_INSTALLATION,
    )

    if (!hardwareBoxChecked) {
        return true
    }

    const hardwareInstallProcess = secondaryProcesses.find(
        secondaryProcess =>
            secondaryProcess.process.id === 'hardware-installation',
    )

    const hardwareInstallFeedback = dfmFeedback.find(feedback =>
        feedback.message.includes('hardware installation'),
    )

    return !!(hardwareInstallProcess && hardwareInstallFeedback)
}

export const isUndercutDataValid = (inspectionData: InspectionDataInput) => {
    const boxChecked = (inspectionData.dfmTags || []).find(
        tag => tag === DFM_TAGS.UNDERCUTS,
    )

    if (!boxChecked) {
        return true
    }

    return (
        !!inspectionData.toolCosts ||
        !!inspectionData.undercuttingToolCostsInCents ||
        !!inspectionData.domesticUndercuttingCosts ||
        !!inspectionData.overseasUndercuttingCosts
    )
}

export const isEdmDataValid = (inspectionData: InspectionDataInput) => {
    const boxChecked = (inspectionData.dfmTags || []).find(
        tag => tag === DFM_TAGS.EDM,
    )

    if (!boxChecked) {
        return true
    }

    return (
        !!inspectionData.edmProductionCostsInCents ||
        !!inspectionData.miscProductionCostInCents ||
        !!inspectionData.edmProductionCosts ||
        !!inspectionData.overseasEdmProductionCosts ||
        !!inspectionData.edmNonRecurringEngineeringCost
    )
}

export const isHobbingDataValid = (inspectionData: InspectionDataInput) => {
    const boxChecked = (inspectionData.dfmTags || []).find(
        tag => tag === DFM_TAGS.HOBBING,
    )

    if (!boxChecked) {
        return true
    }

    return (
        !!inspectionData.miscProductionCostInCents ||
        !!inspectionData.hobbingProductionCosts ||
        !!inspectionData.hobbingNonRecurringEngineeringCost
    )
}

export const isDrillingDataValid = (inspectionData: InspectionDataInput) => {
    const boxChecked = (inspectionData.dfmTags || []).find(
        tag => tag === DFM_TAGS.GUN_DRILLING,
    )

    if (!boxChecked) {
        return true
    }

    return (
        !!inspectionData.miscProductionCostInCents ||
        !!inspectionData.deepHoleProductionCosts ||
        !!inspectionData.deepHoleNonRecurringEngineeringCost
    )
}

export const isDfmTagsValid = (inspectionData: InspectionDataInput) => {
    const { dfmTags } = inspectionData

    const hasMillTags =
        dfmTags.includes(DFM_TAGS.MILL as DfmTags) ||
        dfmTags.includes(DFM_TAGS.FIVE_AXIS_MILL as DfmTags)
    const hasLatheTags =
        dfmTags.includes(DFM_TAGS.LATHE as DfmTags) ||
        dfmTags.includes(DFM_TAGS.FIVE_AXIS_LATHE as DfmTags) ||
        dfmTags.includes(DFM_TAGS.LIVE_TOOLING_LATHE as DfmTags)

    if (hasMillTags && hasLatheTags) {
        return false
    }

    return true
}

export const rushAndFlexAreShortestLeadTimes = (
    inspectionData: Record<string, any>,
) => {
    const { rush, standard, flex, saver } =
        inspectionData?.baseProductionDayOverridesByPriorityId || {}

    if (rush && standard && flex && saver) {
        return rush < standard && flex < saver
    }

    return true
}

export const isScrapRateDataValid = (
    scrapRateData?: ScrapRateDataInput | null,
) => {
    if (!scrapRateData) {
        return true
    }

    const { numParts, numPartsBase } = scrapRateData

    return (
        typeof numParts === 'number' &&
        numParts > -1 &&
        typeof numPartsBase === 'number' &&
        numPartsBase > -1
    )
}

export const getInspectionDataInputInvalidReason = (
    inspectionData: Record<string, any>,
    minMarkupFlagPct: number | null,
) => {
    const {
        miscNonRecurringEngineeringCostInCents,
        miscProductionCostInCents,
        machineTimeMultiplier,
        dfmTags,
        markupData,
        advancedInspectionData,
        scrapRateData,
    } = inspectionData

    if (!isNumber(miscNonRecurringEngineeringCostInCents)) {
        return 'misc NRE'
    }

    if (!isNumber(miscProductionCostInCents)) {
        return 'misc production cost'
    }

    if (!isNumber(machineTimeMultiplier)) {
        return 'machine type multiplier'
    }

    // This isn't fully correct but is a decent enough approximation.
    // We should also double check that each tag is actually of type DfmTag.
    // I think we filter elsewhere, and that isn't guaranteed to always be true, but eh.
    if (!Array.isArray(dfmTags)) {
        return 'dfm tags'
    }

    if (!hasAcceptableMarkups(markupData, minMarkupFlagPct)) {
        return 'markups'
    }

    if (!isAdvancedInspectionDataValid(advancedInspectionData)) {
        return 'advanced inspection'
    }

    if (!isScrapRateDataValid(scrapRateData)) {
        return 'scrap rate'
    }

    return ''
}

export const canCalculatePrice = (
    inspectionData: InspectionDataInput,
    minMarkupFlagPct: number | null,
) => {
    const reason = getInspectionDataInputInvalidReason(
        inspectionData,
        minMarkupFlagPct,
    )

    if (reason) {
        cantCalculateWarning(reason)
        return false
    }

    return true
}

export const canUpdateInspectionData = (
    inspectionData: InspectionDataInput,
    quoteModelRevision: QMRSubset,
    hasFailingDfmFeedback: boolean,
    minMarkupFlagPct: number | null,
) => {
    const reason = getInspectionDataInputInvalidReason(
        inspectionData,
        minMarkupFlagPct,
    )

    if (reason) {
        cantReleaseWarning(reason)
        return false
    }

    if (hasFailingDfmFeedback) {
        return true
    }

    const leadTimeOverridesValid =
        rushAndFlexAreShortestLeadTimes(inspectionData)

    const hardwareInstallDataValid = isHardwareInstallDataValid(
        inspectionData,
        quoteModelRevision.processesDetails?.secondaryProcesses || [],
        quoteModelRevision.dfmFeedback,
    )

    const undercutDataValid = isUndercutDataValid(inspectionData)

    const edmDataValid = isEdmDataValid(inspectionData)

    const hobbingDataValid = isHobbingDataValid(inspectionData)

    const drillingDataValid = isDrillingDataValid(inspectionData)
    const dfmTagsValid = isDfmTagsValid(inspectionData)

    if (!leadTimeOverridesValid) {
        cantReleaseWarning('lead time')
        return false
    }

    if (!hardwareInstallDataValid) {
        cantReleaseWarning('hardware installation')
        return false
    }

    if (!undercutDataValid) {
        cantReleaseWarning('undercut')
        return false
    }

    if (!edmDataValid) {
        cantReleaseWarning('edm')
        return false
    }

    if (!hobbingDataValid) {
        cantReleaseWarning('hobbing')
        return false
    }

    if (!drillingDataValid) {
        cantReleaseWarning('drilling')
        return false
    }

    if (!dfmTagsValid) {
        cantReleaseWarning('dfm tags')
        return false
    }

    return true
}

export const canReleaseInspection = (
    inspectionData: Record<string, any>,
    hasFailingDfmFeedback: boolean,
    isImQuoteType: boolean,
    is3dp: boolean,
) => {
    if (is3dp) {
        return true
    }

    const {
        numSetups,
        camSeconds,
        dfmTags,
        allowedPriorityIds,
        materialStock,
        materialStocks,
        materialCostsInCents,
        // deprecated fields
        overseasMaterialStock,
        materialCostInCents,
        overseasMaterialCostInCents,
    } = inspectionData

    if (hasFailingDfmFeedback) {
        return true
    }

    if (!numSetups && !isImQuoteType) {
        cantReleaseWarning('setups')
        return false
    }

    if (!camSeconds && !isImQuoteType) {
        cantReleaseWarning('cam seconds')
        return false
    }

    if (
        !hasMaterialStockCostForQuotedRegions({
            dfmTags: dfmTags || [],
            allowedPriorityIds: allowedPriorityIds || [],
            materialCostsInCents: materialCostsInCents,
            materialCostInCents,
            overseasMaterialCostInCents,
            materialStocks,
            materialStock: materialStock || {},
            overseasMaterialStock: overseasMaterialStock || {},
        }) &&
        !isImQuoteType
    ) {
        cantReleaseWarning('material stock')
        return false
    }

    return true
}

const stringOrNumDollarToCents = (amount?: string | number | null) => {
    return amount ? Math.round(+amount * CENTS_PER_DOLLAR) || 0 : 0
}

export function generateAdvancedInspectionData(
    form: FormInstance,
    advancedInspectionData?: AdvancedInspectionDataInput | null,
) {
    const adminTouchedOverrides = form.isFieldsTouched(
        Object.values(ADVANCED_INSPECTION_FORM_KEYS),
    )

    // If the admin didn't touch advanced inspection data, send back whatever exists
    if (!adminTouchedOverrides && !advancedInspectionData) {
        return advancedInspectionData
    }

    // Populate with some defaults if admin has entered some advanced inspection data
    const finalAdvancedInspection = Object.assign(
        {},
        advancedInspectionData || {
            numInspectionPoints: 0,
            numSpecialInspectionPoints: 0,
            percentMarkup:
                DEFAULT_ADVANCED_INPECTION_MARKUP_PERCENTAGE / 100 + 1,
        },
    )

    const {
        advancedInspectionNumPoints: numInspectionPoints,
        advancedInspectionNumSpecialPoints: numSpecialInspectionPoints,
        advancedInspectionPercentMarkup: percentMarkup,
        advancedInspectionMpCost: mpCost,
        advancedInspectionNotes: notes,
        advancedInspectionDomesticLeadTimeDays: domesticLeadTimeDays,
        advancedInspectionOverseasLeadTimeDays: overseasLeadTimeDays,
    } = form.getFieldsValue()

    if (numInspectionPoints !== undefined) {
        finalAdvancedInspection.numInspectionPoints =
            parseInt(numInspectionPoints)
    }

    if (numSpecialInspectionPoints !== undefined) {
        finalAdvancedInspection.numSpecialInspectionPoints = parseInt(
            numSpecialInspectionPoints,
        )
    }

    if (percentMarkup) {
        finalAdvancedInspection.percentMarkup = percentMarkup / 100 + 1
    }

    if (notes !== undefined) {
        finalAdvancedInspection.notes = notes
    }

    if (domesticLeadTimeDays) {
        finalAdvancedInspection.domesticLeadTimeDays =
            parseInt(domesticLeadTimeDays)
    }

    if (overseasLeadTimeDays) {
        finalAdvancedInspection.overseasLeadTimeDays =
            parseInt(overseasLeadTimeDays)
    }

    if (mpCost !== undefined) {
        finalAdvancedInspection.mpCost = stringOrNumDollarToCents(mpCost)
    }

    return finalAdvancedInspection
}

const getLeadTimeOverrideData = (
    form: FormInstance,
    calculatingPendingPrice: boolean,
    is3dp: boolean,
) => {
    const rushLeadTimeKey =
        QUOTING_FORM_KEYS.LEAD_TIME_OVERRIDE + PRIORITYID_RUSH
    const flexLeadTimeKey =
        QUOTING_FORM_KEYS.LEAD_TIME_OVERRIDE + PRIORITYID_FLEX

    const overrideFields = [
        rushLeadTimeKey,
        QUOTING_FORM_KEYS.LEAD_TIME_OVERRIDE + PRIORITYID_STANDARD,
        QUOTING_FORM_KEYS.LEAD_TIME_OVERRIDE + PRIORITYID_ECONOMY,
        flexLeadTimeKey,
        QUOTING_FORM_KEYS.LEAD_TIME_OVERRIDE + PRIORITYID_SAVER,
        QUOTING_FORM_KEYS.LEAD_TIME_OVERRIDE + PRIORITYID_OVERSEAS,
    ]

    const {
        [rushLeadTimeKey]: rush,
        [QUOTING_FORM_KEYS.LEAD_TIME_OVERRIDE + PRIORITYID_STANDARD]: standard,
        [QUOTING_FORM_KEYS.LEAD_TIME_OVERRIDE + PRIORITYID_ECONOMY]: economy,
        [flexLeadTimeKey]: flex,
        [QUOTING_FORM_KEYS.LEAD_TIME_OVERRIDE + PRIORITYID_SAVER]: saver,
        [QUOTING_FORM_KEYS.LEAD_TIME_OVERRIDE + PRIORITYID_OVERSEAS]: overseas,
    } = form.getFieldsValue(overrideFields)

    // In order for dynamic lead time to calculate overrides on our behalf, we will only send the
    // fastest lead times in the case that an admin has touched the field so dynamic lead time
    // can calculate the remaining set of production days
    const pendingLeadTimeData: { rush?: number; flex?: number } = {}

    if (form.isFieldTouched(rushLeadTimeKey)) {
        pendingLeadTimeData.rush = rush
    }

    if (form.isFieldTouched(flexLeadTimeKey)) {
        pendingLeadTimeData.flex = flex
    }

    if (calculatingPendingPrice) {
        return pendingLeadTimeData
    }

    return {
        rush,
        standard,
        economy,
        flex: is3dp ? null : flex,
        saver: is3dp ? null : saver,
        overseas: is3dp ? null : overseas,
    }
}

const getMarkupOverrideData = ({
    form,
    dfmMarkupData,
    markupData,
}: {
    form: FormInstance
    dfmMarkupData?: PendingMarkupDataInput | null
    markupData?: MarkupDataType | null
}) => {
    const markupOverrides = { ...dfmMarkupData } as PendingMarkupDataInput

    const priorities = [...DOMESTIC_PRIORITIES, ...OVERSEAS_PRIORITIES].filter(
        id => id !== PRIORITYID_SAMEDAY,
    ) as (keyof PendingMarkupDataInput | typeof PRIORITYID_PREMIUM)[]

    priorities.forEach(priority => {
        if (priority === PRIORITYID_PREMIUM) {
            return
        }
        const field = QUOTING_FORM_KEYS.MARKUP_OVERRIDE + priority
        const value = form.getFieldValue(field)
        const markupDataValue = ((markupData?.[priority] || 0) * 100).toFixed(2)

        if (value !== markupDataValue) {
            const markupFloat = parseFloat(value)

            markupOverrides[priority] = Number.isFinite(markupFloat)
                ? markupFloat / 100
                : null
        }
    })

    return markupOverrides
}

const getScrapRateData = (
    formNumParts?: string | number,
    formNumPartsBase?: string | number,
): ScrapRateDataInput | undefined => {
    if (
        !isNumber(formNumParts) &&
        !formNumParts &&
        !isNumber(formNumPartsBase) &&
        !formNumPartsBase
    ) {
        return
    }

    return {
        numParts: formNumParts && parseInt(`${formNumParts}`),
        numPartsBase: formNumPartsBase && parseInt(`${formNumPartsBase}`),
    } as any // doing this intentionally to let the save do validation
}

export const getDfmTags = ({
    inspectionData,
    formDfmTags,
    dfmFeedback,
}: {
    inspectionData: InspectionDataInput
    formDfmTags: string[]
    dfmFeedback: DfmFeedbackSubset[]
}) => {
    /*
        The tags in the `dfmTags` array are in the shape they are stored in the database (i.e. `Gun drilling`)
        The GraphQL enum type wants them in UPPER_SNAKE_CASE, so let's get the tags in that shape

        Here we turn dfmTag labels into the enum KEYS
        Keep DFM_TAGS if the corresponding label is in the dfmTags array
    */

    const cleanedDfmTags = Object.values(DFM_TAGS).filter(tag => {
        // Always remove the auto-quoted checkbox if an admin is saving/releasing a part
        if (tag === 'AUTO') {
            return false
        }
        return formDfmTags.includes(DFM_TAGS_MAP[tag])
    })

    if (cleanedDfmTags.includes(DFM_TAGS.FIVE_AXIS_LATHE)) {
        const index = cleanedDfmTags.indexOf(DFM_TAGS.FIVE_AXIS_LATHE)

        cleanedDfmTags.splice(index, 1)

        if (!cleanedDfmTags.includes(DFM_TAGS.FIVE_AXIS)) {
            cleanedDfmTags.push(DFM_TAGS.FIVE_AXIS)
        }

        if (!cleanedDfmTags.includes(DFM_TAGS.LATHE)) {
            cleanedDfmTags.push(DFM_TAGS.LATHE)
        }
    }

    if (cleanedDfmTags.includes(DFM_TAGS.FIVE_AXIS_MILL)) {
        const index = cleanedDfmTags.indexOf(DFM_TAGS.FIVE_AXIS_MILL)

        cleanedDfmTags.splice(index, 1)

        if (!cleanedDfmTags.includes(DFM_TAGS.FIVE_AXIS)) {
            cleanedDfmTags.push(DFM_TAGS.FIVE_AXIS)
        }

        if (!cleanedDfmTags.includes(DFM_TAGS.MILL)) {
            cleanedDfmTags.push(DFM_TAGS.MILL)
        }
    }

    if (
        cleanedDfmTags.includes(DFM_TAGS.LIVE_TOOLING_LATHE) &&
        !cleanedDfmTags.includes(DFM_TAGS.LATHE)
    ) {
        cleanedDfmTags.push(DFM_TAGS.LATHE)
    }
    /* Automatically check some boxes if there is some data present */
    const {
        deepHoleProductionCosts,
        deepHoleNonRecurringEngineeringCost,
        hobbingProductionCosts,
        hobbingNonRecurringEngineeringCost,
        numTightTolerances,
        numVeryTightTolerances,
        edmStepItems,
        reamerCostsInCents,
        tapToolCostsInCents,
        toolCostsInCents,
        undercuttingToolCostsInCents,
        // deprecated fields
        domesticReamerCosts,
        overseasReamerCosts,
        domesticTapToolCosts,
        overseasTapToolCosts,
        toolCosts,
        overseasToolCosts,
        domesticUndercuttingCosts,
        overseasUndercuttingCosts,
    } = inspectionData

    if (
        (deepHoleProductionCosts || deepHoleNonRecurringEngineeringCost) &&
        !cleanedDfmTags.includes(DFM_TAGS.GUN_DRILLING)
    ) {
        cleanedDfmTags.push(DFM_TAGS.GUN_DRILLING)
    }

    if (edmStepItems && !cleanedDfmTags.includes(DFM_TAGS.EDM)) {
        cleanedDfmTags.push(DFM_TAGS.EDM)
    }

    if (!edmStepItems && cleanedDfmTags.includes(DFM_TAGS.EDM)) {
        const index = cleanedDfmTags.indexOf(DFM_TAGS.EDM)

        cleanedDfmTags.splice(index, 1)
    }

    if (
        (hobbingProductionCosts || hobbingNonRecurringEngineeringCost) &&
        !cleanedDfmTags.includes(DFM_TAGS.HOBBING)
    ) {
        cleanedDfmTags.push(DFM_TAGS.HOBBING)
    }

    const hasSpecialTooling =
        reamerCostsInCents ||
        tapToolCostsInCents ||
        toolCostsInCents ||
        undercuttingToolCostsInCents ||
        // deprecated fields
        domesticReamerCosts ||
        overseasReamerCosts ||
        domesticTapToolCosts ||
        overseasTapToolCosts ||
        toolCosts ||
        overseasToolCosts ||
        domesticUndercuttingCosts ||
        overseasUndercuttingCosts

    if (
        hasSpecialTooling &&
        !cleanedDfmTags.includes(DFM_TAGS.SPECIAL_TOOLING)
    ) {
        cleanedDfmTags.push(DFM_TAGS.SPECIAL_TOOLING)
    }

    if (
        (undercuttingToolCostsInCents?.usa ||
            undercuttingToolCostsInCents?.china ||
            domesticUndercuttingCosts ||
            overseasUndercuttingCosts) &&
        !cleanedDfmTags.includes(DFM_TAGS.UNDERCUTS)
    ) {
        cleanedDfmTags.push(DFM_TAGS.UNDERCUTS)
    }

    if (
        !undercuttingToolCostsInCents?.usa &&
        !undercuttingToolCostsInCents?.china &&
        !domesticUndercuttingCosts &&
        !overseasUndercuttingCosts &&
        cleanedDfmTags.includes(DFM_TAGS.UNDERCUTS)
    ) {
        const index = cleanedDfmTags.indexOf(DFM_TAGS.UNDERCUTS)

        cleanedDfmTags.splice(index, 1)
    }

    if (
        (numTightTolerances || numVeryTightTolerances) &&
        !cleanedDfmTags.includes(DFM_TAGS.TIGHT_TOLERANCES)
    ) {
        cleanedDfmTags.push(DFM_TAGS.TIGHT_TOLERANCES)
    }

    const hasEngravingDfm = dfmFeedback.some(
        ({ message: dfmMessage }) =>
            dfmMessage.includes(
                'This part will be machine or laser engraved',
            ) ||
            dfmMessage.includes('This part will be machine engraved') ||
            dfmMessage.includes('This part will be laser engraved'),
    )

    if (hasEngravingDfm && !cleanedDfmTags.includes(DFM_TAGS.ENGRAVING)) {
        cleanedDfmTags.push(DFM_TAGS.ENGRAVING)
    }

    return cleanedDfmTags as DfmTags[] // I couldn't manage to type this such that it accepted this so yolo.
}

export function generateQuantityRangeData({ form }: { form: FormInstance }) {
    const {
        quantityRange,
        isQuantityRangeUnavailable,
    }: Partial<{ [K in QuotingFormKeysType]: any }> = form.getFieldsValue()
    const quantityRanges = quantityRange?.split('-')

    return {
        quantityLowerRange: quantityRanges ? parseInt(quantityRanges[0]) : 0,
        quantityUpperRange: quantityRanges ? parseInt(quantityRanges[1]) : 0,
        isQuantityRangeAvailable: !isQuantityRangeUnavailable,
    }
}

export const generateDfmTags = (tags?: string[]): string[] => {
    const generatedDfmTags = (tags && [...tags]) || []

    if (
        generatedDfmTags.includes(DFM_TAGS_MAP[DFM_TAGS.MILL]) &&
        generatedDfmTags.includes(DFM_TAGS_MAP[DFM_TAGS.FIVE_AXIS])
    ) {
        generatedDfmTags.push(DFM_TAGS_MAP[DFM_TAGS.FIVE_AXIS_MILL])
    }

    if (
        generatedDfmTags.includes(DFM_TAGS_MAP[DFM_TAGS.LATHE]) &&
        generatedDfmTags.includes(DFM_TAGS_MAP[DFM_TAGS.FIVE_AXIS])
    ) {
        generatedDfmTags.push(DFM_TAGS_MAP[DFM_TAGS.FIVE_AXIS_LATHE])
    }

    if (
        generatedDfmTags.includes(DFM_TAGS_MAP[DFM_TAGS.LIVE_TOOLING_LATHE]) &&
        !generatedDfmTags.includes(DFM_TAGS_MAP[DFM_TAGS.LATHE])
    ) {
        generatedDfmTags.push(DFM_TAGS_MAP[DFM_TAGS.LATHE])
    }

    return generatedDfmTags
}

const generateEdmStepItems = (form: FormInstance) => {
    // A hacky solution until the AdminQuotingPage is upgraded to antd v5 and is able to use dynamic nested forms
    const formValues = form.getFieldsValue()

    const steps = Object.keys(formValues)
        .filter(key => key.includes('-edm-type'))
        .map(et => Number(et.split('-')[0]))

    const { stepItems } = generateStepItems(form, steps)

    return { stepItems }
}

export function generateInspectionData({
    form,
    quoteModelRevision,
    calculatingPendingPrice = false,
    isImQuoteType,
    markupData,
}: {
    form: FormInstance
    quoteModelRevision: QMRSubset
    calculatingPendingPrice?: boolean
    isImQuoteType: boolean
    markupData?: MarkupDataType | null
}) {
    const {
        allowedRegions,
        camSeconds,
        chinaMaterialCostInCents,
        classANonRecurringEngineeringCost,
        classAPartComplexity,
        classAProductionCosts,
        classARequired,
        dfmTags,
        deepHoleNonRecurringEngineeringCost,
        deepHoleProductionCosts,
        domesticReamerCosts,
        domesticTapToolCosts,
        domesticUndercuttingCosts,
        edmProductionCosts,
        chinaEdmProductionCosts,
        indiaEdmProductionCosts,
        hobbingNonRecurringEngineeringCost,
        hobbingProductionCosts,
        indiaMaterialCostInCents,
        machineTimeMultiplier,
        materialCostInCents,
        machiningSeconds,
        miscNonRecurringEngineeringCostInCents,
        miscProductionCostInCents,
        notes,
        numNonStandardFixtures,
        numSetups,
        numTightTolerances,
        numThreadedFeatures,
        numVeryTightTolerances,
        overseasReamerCosts,
        overseasTapToolCosts,
        overseasToolCosts,
        overseasUndercuttingCosts,
        scrapMaterial,
        scrapRateNumParts,
        scrapRateNumPartsBase,
        toolCosts,
        tabbingMachiningSeconds,
    }: Partial<{ [K in QuotingFormKeysType]: any }> = form.getFieldsValue()

    const fallbackInspectionData = {
        miscNonRecurringEngineeringCostInCents: 0,
        miscProductionCostInCents: 0,
        machineTimeMultiplier: 1,
        dfmTags: [],
    }

    const defaultInspectionData: InspectionDataInput = {
        ...(quoteModelRevision.modelConfiguration?.dfmInspection // to keep around legacy fields I guess
            ?.inspectionData || fallbackInspectionData),
    }

    defaultInspectionData.advancedInspectionData =
        generateAdvancedInspectionData(
            form,
            defaultInspectionData.advancedInspectionData,
        )

    if (isImQuoteType) {
        return defaultInspectionData
    }

    let inspectionData: InspectionDataInput = {
        ...defaultInspectionData,
        allowedPriorityIds: [], //default
        allowedInRegion: {
            usa: allowedRegions.includes(OPERATION_REGION_USA),
            china: allowedRegions.includes(OPERATION_REGION_CHINA),
            india: allowedRegions.includes(OPERATION_REGION_CHINA)
                ? allowedRegions.includes(OPERATION_REGION_INDIA)
                : false,
        },
        deepHoleNonRecurringEngineeringCost: stringOrNumDollarToCents(
            deepHoleNonRecurringEngineeringCost,
        ),
        deepHoleProductionCosts: stringOrNumDollarToCents(
            deepHoleProductionCosts,
        ),
        hobbingNonRecurringEngineeringCost: stringOrNumDollarToCents(
            hobbingNonRecurringEngineeringCost,
        ),
        hobbingProductionCosts: stringOrNumDollarToCents(
            hobbingProductionCosts,
        ),
        miscNonRecurringEngineeringCostInCents: stringOrNumDollarToCents(
            miscNonRecurringEngineeringCostInCents,
        ),
        miscProductionCostInCents: stringOrNumDollarToCents(
            miscProductionCostInCents,
        ),
        machineTimeMultiplier: +machineTimeMultiplier || 1,
        notes,
        numNonStandardFixtures: parseFloat(numNonStandardFixtures),
        numSetups: parseInt(numSetups),
        numTightTolerances: parseInt(numTightTolerances),
        numThreadedFeatures: parseInt(numThreadedFeatures),
        numVeryTightTolerances: parseInt(numVeryTightTolerances),
        scrapRateData: getScrapRateData(
            scrapRateNumParts,
            scrapRateNumPartsBase,
        ),
    }

    inspectionData = {
        ...inspectionData,
        edmProductionCostsInCents: {
            [OPERATION_REGION_USA]:
                stringOrNumDollarToCents(edmProductionCosts),
            [OPERATION_REGION_CHINA]: stringOrNumDollarToCents(
                chinaEdmProductionCosts,
            ),
            [OPERATION_REGION_INDIA]: stringOrNumDollarToCents(
                indiaEdmProductionCosts,
            ),
        },
        reamerCostsInCents: {
            [OPERATION_REGION_USA]:
                stringOrNumDollarToCents(domesticReamerCosts),
            [OPERATION_REGION_CHINA]:
                stringOrNumDollarToCents(overseasReamerCosts),
            [OPERATION_REGION_INDIA]:
                stringOrNumDollarToCents(overseasReamerCosts),
        },
        tapToolCostsInCents: {
            [OPERATION_REGION_USA]:
                stringOrNumDollarToCents(domesticTapToolCosts),
            [OPERATION_REGION_CHINA]:
                stringOrNumDollarToCents(overseasTapToolCosts),
            [OPERATION_REGION_INDIA]:
                stringOrNumDollarToCents(overseasTapToolCosts),
        },
        toolCostsInCents: {
            [OPERATION_REGION_USA]: stringOrNumDollarToCents(toolCosts),
            [OPERATION_REGION_CHINA]:
                stringOrNumDollarToCents(overseasToolCosts),
            [OPERATION_REGION_INDIA]:
                stringOrNumDollarToCents(overseasToolCosts),
        },
        undercuttingToolCostsInCents: {
            [OPERATION_REGION_USA]: stringOrNumDollarToCents(
                domesticUndercuttingCosts,
            ),
            [OPERATION_REGION_CHINA]: stringOrNumDollarToCents(
                overseasUndercuttingCosts,
            ),
            [OPERATION_REGION_INDIA]: stringOrNumDollarToCents(
                overseasUndercuttingCosts,
            ),
        },
    }

    if (scrapMaterial) {
        inspectionData.scrapMaterialCostsInCents = {
            [OPERATION_REGION_USA]:
                stringOrNumDollarToCents(materialCostInCents),
            [OPERATION_REGION_CHINA]: stringOrNumDollarToCents(
                chinaMaterialCostInCents,
            ),
            [OPERATION_REGION_INDIA]: stringOrNumDollarToCents(
                indiaMaterialCostInCents,
            ),
        }
    }
    if (!scrapMaterial) {
        inspectionData.scrapMaterialCostsInCents = null
    }

    if (form.isFieldTouched(QUOTING_FORM_KEYS.DOMESTIC_MATERIAL_COST)) {
        inspectionData.materialCostsInCents = {
            ...inspectionData.materialCostsInCents,
            usa: stringOrNumDollarToCents(materialCostInCents),
        }
    }

    if (form.isFieldTouched(QUOTING_FORM_KEYS.CHINA_MATERIAL_COST)) {
        inspectionData.materialCostsInCents = {
            ...inspectionData.materialCostsInCents,
            china: stringOrNumDollarToCents(chinaMaterialCostInCents),
        }
    }

    if (form.isFieldTouched(QUOTING_FORM_KEYS.INDIA_MATERIAL_COST)) {
        inspectionData.materialCostsInCents = {
            ...inspectionData.materialCostsInCents,
            india: stringOrNumDollarToCents(indiaMaterialCostInCents),
        }
    }

    const { stepItems } = generateEdmStepItems(form)

    inspectionData.edmStepItems = stepItems

    if (classARequired) {
        inspectionData.classA = {
            required: classARequired,
            partComplexity: classAPartComplexity.toLowerCase(),
            nonRecurringCostsInCents: {
                [OPERATION_REGION_USA]: stringOrNumDollarToCents(
                    classANonRecurringEngineeringCost,
                ),
                [OPERATION_REGION_CHINA]: 0,
                [OPERATION_REGION_INDIA]: 0,
            },
            productionCostsInCents: {
                [OPERATION_REGION_USA]: stringOrNumDollarToCents(
                    classAProductionCosts,
                ),
                [OPERATION_REGION_CHINA]: 0,
                [OPERATION_REGION_INDIA]: 0,
            },
        }
    } else {
        inspectionData.classA = {
            required: classARequired,
            partComplexity: null,
            productionCostsInCents: {
                [OPERATION_REGION_USA]: 0,
                [OPERATION_REGION_CHINA]: 0,
                [OPERATION_REGION_INDIA]: 0,
            },
            nonRecurringCostsInCents: {
                [OPERATION_REGION_USA]: 0,
                [OPERATION_REGION_CHINA]: 0,
                [OPERATION_REGION_INDIA]: 0,
            },
        }
    }

    // Allow system to populate these values if the admin has not touched these fields
    if (form.isFieldTouched(QUOTING_FORM_KEYS.MACHINING_SECONDS)) {
        inspectionData.machiningSeconds = getSeconds(machiningSeconds)
    }

    if (form.isFieldTouched(QUOTING_FORM_KEYS.TABBING_MACHINING_SECONDS)) {
        const tabbingSeconds = getSeconds(tabbingMachiningSeconds)

        if (
            inspectionData?.specialFixturingParameters?.tabbingParameters
                ?.tabbingMachiningSeconds
        ) {
            inspectionData.specialFixturingParameters.tabbingParameters.tabbingMachiningSeconds =
                tabbingSeconds
        } else if (tabbingMachiningSeconds) {
            inspectionData.tabbingMachiningSeconds = tabbingSeconds
        }
    }

    if (form.isFieldTouched(QUOTING_FORM_KEYS.CAM_SECONDS)) {
        inspectionData.camSeconds = getSeconds(camSeconds)
    }

    const cleanedDfmTags = getDfmTags({
        inspectionData,
        formDfmTags: dfmTags,
        dfmFeedback: quoteModelRevision.dfmFeedback,
    })

    inspectionData.dfmTags = cleanedDfmTags

    if (!calculatingPendingPrice) {
        // If an admin touched the checkboxes and we are also automatically checking some
        // the form won't update. Here we set the form so admins can visually see
        // the changes

        const tags = cleanedDfmTags.map(
            tag => DFM_TAGS_MAP[tag as keyof typeof DFM_TAGS_MAP],
        )

        const generatedDfmTags = generateDfmTags(tags)

        form.setFieldsValue({
            [QUOTING_FORM_KEYS.DFM_TAGS]: generatedDfmTags,
        })
    }

    const processId = quoteModelRevision.modelConfiguration?.process?.id

    inspectionData.baseProductionDayOverridesByPriorityId =
        getLeadTimeOverrideData(
            form,
            calculatingPendingPrice,
            processId === PROCESSID_3DP,
        )

    inspectionData.markupData = getMarkupOverrideData({
        form,
        dfmMarkupData: inspectionData.markupData,
        markupData,
    })

    const priorityIds: string[] = []

    if (allowedRegions.includes(OPERATION_REGION_USA)) {
        priorityIds.push(
            PRIORITYID_RUSH,
            PRIORITYID_STANDARD,
            PRIORITYID_ECONOMY,
        )
    }

    if (allowedRegions.includes(OPERATION_REGION_CHINA)) {
        priorityIds.push(PRIORITYID_FLEX, PRIORITYID_SAVER, PRIORITYID_OVERSEAS)
    }

    inspectionData.allowedPriorityIds = priorityIds

    return inspectionData
}
